import apiRequest from "../../utils/apiRequest";

/**
 * Services HTTP des domaines Acts et ActsPricePerInsurer (Phase 2.F).
 *
 * <p>Aligné sur le contrat back :
 * <ul>
 *   <li>POST /acts → 201 + Long (id).</li>
 *   <li>PUT /acts/{id} → Long (id).</li>
 *   <li>DELETE /acts/{id} → void.</li>
 *   <li>GET /acts/{id} → ActsResponse.</li>
 *   <li>GET /acts?search&page&size → Map paginée
 *       (PageMapper.toMap : content/totalElements/...). Remplace
 *       l'ancien POST /acts/get-filter supprimé en Phase 14.</li>
 *   <li>POST /acts-price-insurer → Long.</li>
 *   <li>PUT /acts-price-insurer/{id} → Long.</li>
 *   <li>DELETE /acts-price-insurer/{id} → void.</li>
 *   <li>GET /acts-price-insurer/{id} → ActsPricePerInsurerResponse.</li>
 *   <li>GET /acts-price-insurer/by-insurer/{insurerId} →
 *       List&lt;ActsPricePerInsurerResponse&gt;. Remplace
 *       l'ancien POST /acts-price-insurer/get-filter.</li>
 * </ul>
 *
 * <p>Toutes les fonctions retournent l'enveloppe uniforme apiRequest :
 * {@code { ok, status, data, message?, error? }}.
 */

/* ------------------------------------------------------------------ */
/* Acts                                                                */
/* ------------------------------------------------------------------ */

export const createActs = async (payload) => {
  return apiRequest.post("/acts", payload);
};

export const updateActs = async ({ data, id } = {}) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.put(`/acts/${id}`, data);
};

export const deleteActs = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.del(`/acts/${id}`);
};

export const getActsById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/acts/${id}`);
};

/**
 * Catalogue paginé d'actes — remplace l'ancien {@code POST /acts/get-filter}.
 *
 * @param {object} [params={}]
 * @param {string} [params.search='']
 * @param {number} [params.page=0]
 * @param {number} [params.size=20]
 * @returns enveloppe avec {@code data} = {@code {content, totalElements, ...}}.
 */
export const getActs = async ({ search = "", page = 0, size = 20 } = {}) => {
  const qs = new URLSearchParams({ search, page: String(page), size: String(size) }).toString();
  return apiRequest.get(`/acts?${qs}`);
};

/**
 * @deprecated alias rétro-compatible vers {@link getActs}.
 *
 * <p>L'ancien {@code POST /acts/get-filter} acceptait un objet de filtre
 * arbitraire et renvoyait un tableau plat. Pour préserver l'API des
 * consommateurs (tabs Consultation, hooks react-query qui font
 * {@code data.map(...)}), cet alias <strong>aplatit</strong> la réponse
 * paginée : il extrait {@code page.content} et le retourne dans
 * {@code data}, comme avant.
 */
export const filterActs = async (payload = {}) => {
  const search =
    payload?.search ?? payload?.label ?? payload?.q ?? "";
  const result = await getActs({ search });
  if (!result?.ok) return result;
  return { ...result, data: result.data?.content ?? [] };
};

/**
 * Lookup léger {id, label, price, actsTypeName} pour les
 * selects/comboboxes — endpoint dédié, plus économique que
 * {@link getActs} qui charge le DTO complet paginé. La table Acts est
 * petite : le back renvoie tout en une seule requête, triée par label.
 * À cacher 30 min côté client.
 *
 * <p>`price` et `actsTypeName` sont inclus pour permettre à l'onglet
 * « Bons » de calculer les lignes de facture sans avoir besoin du DTO
 * complet (fallback tarif sans assureur, choix du % de couverture
 * examen vs consultation).
 *
 * @returns enveloppe avec {@code data} = {@code ActsLookupItem[]}.
 */
export const getActsLookup = async () => {
  return apiRequest.get("/acts/lookup");
};

/**
 * Lookup d'un acte unique par id — utilisé pour précharger le label
 * affiché dans un select en édition (alternative au cache déjà chargé).
 *
 * @returns enveloppe avec {@code data} = {@code LookupItem} ou 404.
 */
export const getActsLookupById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/acts/lookup/${id}`);
};

/* ------------------------------------------------------------------ */
/* ActsPricePerInsurer                                                  */
/* ------------------------------------------------------------------ */

export const createActsPriceInsurer = async (payload) => {
  return apiRequest.post("/acts-price-insurer", payload);
};

export const updateActsPriceInsurer = async (payload = {}) => {
  const id = payload?.id;
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.put(`/acts-price-insurer/${id}`, payload);
};

export const deleteActsPriceInsurer = async (payload) => {
  // Compat : accepte soit l'objet historique {id}, soit l'id directement.
  const id = typeof payload === "object" && payload !== null ? payload?.id : payload;
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.del(`/acts-price-insurer/${id}`);
};

export const getActsPriceInsurerById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/acts-price-insurer/${id}`);
};

/**
 * Liste plate des tarifs configurés pour un assureur — remplace
 * {@code POST /acts-price-insurer/get-filter}. Pas de pagination.
 */
export const getActsPriceInsurerByInsurer = async (insurerId) => {
  if (insurerId == null) {
    return { ok: false, status: 0, message: "insurerId manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/acts-price-insurer/by-insurer/${insurerId}`);
};

/**
 * @deprecated alias rétro-compatible : certains anciens consommateurs
 * utilisaient {@code createActsInsurer} / {@code updateActsInsurer} /
 * {@code deleteActsInsurer}. On les conserve pour ne pas casser les
 * imports historiques.
 */
export const createActsInsurer = (payload) => createActsPriceInsurer(payload);
export const updateActsInsurer = ({ data, id } = {}) =>
  updateActsPriceInsurer({ ...(data || {}), id });
export const deleteActsInsurer = (id) => deleteActsPriceInsurer(id);
